import { useEffect, useMemo, useState } from 'react';

import type { VideaNotesAppointment } from '@/modules/videaNotes/helpers';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

type SortField = 'patient' | 'appointment' | 'operatory' | 'provider';
type SortDirection = 'asc' | 'desc';

export const useAmbientSorting = (
  appointmentsWithNotes: (VideaNotesAppointment & {
    notes: ClinicalNote[];
  })[],
  isRecentNotesView: boolean = false,
) => {
  // Sorting state - default to desc when in recent notes view
  const [sortField, setSortField] = useState<SortField>('appointment');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    isRecentNotesView ? 'desc' : 'asc',
  );

  // Update sort direction when switching between normal and recent notes view
  useEffect(() => {
    // Only auto-adjust when sorting by appointment field
    if (sortField === 'appointment') {
      setSortDirection(isRecentNotesView ? 'desc' : 'asc');
    }
  }, [isRecentNotesView, sortField]);

  // Sort appointments with notes
  const sortedAppointments = useMemo(() => {
    const sorted = [...appointmentsWithNotes].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'patient': {
          const nameA =
            `${a.patient.firstName} ${a.patient.lastName}`.toLowerCase();
          const nameB =
            `${b.patient.firstName} ${b.patient.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB) * direction;
        }
        case 'appointment': {
          // Use noteCreatedAt for recent notes view, otherwise use appointment start time
          const dateA = isRecentNotesView && a.appointment.noteCreatedAt
            ? new Date(a.appointment.noteCreatedAt).getTime()
            : new Date(a.appointment.start).getTime();
          const dateB = isRecentNotesView && b.appointment.noteCreatedAt
            ? new Date(b.appointment.noteCreatedAt).getTime()
            : new Date(b.appointment.start).getTime();
          return (dateA - dateB) * direction;
        }
        case 'operatory': {
          const _a = a.appointment.operatory?.name;
          const _b = b.appointment.operatory?.name;

          // no change if both null
          if (!_a && !_b) {
            return 0;
          }

          // nulls always at bottom
          if (!_a) {
            return 1;
          }
          if (!_b) {
            return -1;
          }

          return _a.localeCompare(_b) * direction;
        }
        case 'provider': {
          const _a = a.appointment.provider ? `${a.appointment.provider.firstName ?? ''} ${a.appointment.provider.lastName ?? ''}`.trim() : null;
          const _b = b.appointment.provider ? `${b.appointment.provider.firstName ?? ''} ${b.appointment.provider.lastName ?? ''}`.trim() : null;

          // no change if both null
          if (!_a && !_b) {
            return 0;
          }

          // nulls always at bottom
          if (!_a) {
            return 1;
          }
          if (!_b) {
            return -1;
          }

          return _a.localeCompare(_b) * direction;
        }
        default:
          return 0;
      }
    });

    return sorted;
  }, [appointmentsWithNotes, sortField, sortDirection, isRecentNotesView]);

  const handleSort = (field: string, direction: SortDirection) => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  return {
    sortedAppointments,
    sortField,
    sortDirection,
    handleSort,
  };
};
